import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import { SubscriptionTier } from '@Types/fish';
import { PaginatedResponse } from '@Types/api';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import { ScrollText } from 'lucide-react';
import { useEffect, useState } from 'react';
import service from '@Service';

type TableStatus = 'idle' | 'pending' | 'error' | 'success';

const subscriptionTierColumnConfigs: ColumnConfig<SubscriptionTier>[] = [
  {
    id: 'title',
    headerLabel: 'Title',
    accessor: (row) => row?.title || '-',
    type: 'text',
    size: 120,
    enableSorting: false,
  },
  {
    id: 'description',
    headerLabel: 'Description',
    accessor: (row) => row?.description || '-',
    type: 'text',
    size: 120,
    enableSorting: false,
  },
  {
    id: 'createdOn',
    headerLabel: 'Created On',
    accessor: (row) => row?.createdAt || '-',
    type: 'text',
    cell: (info) => {
      const date = info.getValue();
      if (!date) return 'N/A';
      const dt = DateTime.fromISO(date);
      const dateString = dt.toLocaleString(DateTime.DATETIME_MED);
      return dateString || 'N/A';
    },
    size: 120,
    enableSorting: false,
  },
  {
    id: 'createdBy',
    headerLabel: 'Created By',
    accessor: (row) => row?.createdByUser?.name || '-',
    type: 'text',
    size: 120,
    enableSorting: false,
  },
];

export default function SubscriptionTierList() {
  const navigate = useNavigate();
  const [subscriptionTiers, setSubscriptionTiers] = useState([]);
  const [meta, setMeta] = useState(null);
  const [status, setStatus] = useState<TableStatus>('idle');

  const controller = useResourceTableController<SubscriptionTier>({
    columnsConfig: subscriptionTierColumnConfigs,
    defaultSort: {
      id: 'title',
      desc: false,
    },
    baseSearchParams: () => ({}),
    buildFilterParams: () => ({}),
    initialPageSize: 10,
  });

  useEffect(() => {
    const retrieveSubscriptionTiers = async () => {
      try {
        setStatus('pending');

        const data: PaginatedResponse<SubscriptionTier[]> =
          await service.getSubscriptionTiersWithMeta({
            'page[number]': controller.pageIndex + 1,
            'page[size]': controller.pageSize,
          });

        if (!data?.data) throw new Error('Failed to fetch subscription tiers');

        const sorted = data?.data
          ?.slice()
          ?.sort((a, b) => (a?.title ?? '').localeCompare(b?.title ?? ''));

        setSubscriptionTiers(sorted);
        setMeta(data.meta);
        setStatus('success');
      } catch (err) {
        console.error(err);
        setStatus('error');
      }
    };
    retrieveSubscriptionTiers();
  }, [controller.pageIndex, controller.pageSize, subscriptionTiers?.length]);

  const rows: SubscriptionTier[] = subscriptionTiers ?? [];

  return (
    <ResourceTableView<SubscriptionTier>
      label="Subscription Tiers"
      summaryIcon={<ScrollText />}
      controller={controller}
      data={rows}
      meta={meta}
      status={status}
      onRowClick={(row) => navigate(`/subscription-tiers/${row.id}`)}
    />
  );
}
